// External Imports
import { Fragment, useContext, useEffect, useState } from "react";
import { requestAdminAPI } from "../../api/posh-api";
import { useSearchParams } from "react-router-dom";
import ImageSelector from "../UI/ImageSelector";
import DetailPair from "../UI/DetailPair";
import SelectPair from "../UI/SelectPair";
import { DataContext } from "../../context/data-context";
import Heading from "../UI/Heading";
import { MdCheck, MdClose, MdEdit, MdKey, MdRefresh, MdSettings } from "react-icons/md";
import AttendanceGraph from "./AttendanceGraph";
import LoadingSpinner from "../UI/LoadingSpinner";
import { getGeolocation } from "../../util/getGeolocation";
import { UtilityContext } from "../../context/util-context";
import uploadFile from "../../util/upload-file";
import LinkPair from "../UI/LinkPair";
import { toTitleCase } from "../../util/format-text";
import ManageEmployeeModal from "./ManageEmployeeModal";
import AuditLogs from "../UI/AuditLogs";

// Internal Imports

/** ========================================================================
 * * EmployeeInfo Component
 * This component renders a page to show an employees information.
 * ========================================================================== */

export default function EmployeeInfo() {
  const { setWarning, navigate } = useContext(UtilityContext);
  const { locations, admin } = useContext(DataContext);
  // Get Employee ID from URL
  const [searchParams] = useSearchParams();
  const id = searchParams.get("id");

  // Initialize States
  const [loading, setLoading] = useState(true);
  const [employee, setEmployee] = useState({});
  const [edit, setEdit] = useState(false);
  const [manage, setManage] = useState(false);

  /* ====================== FETCH EMPLOYEE  ====================== */
  const fetchEmployee = async () => {
    try {
      setLoading(true);
      const response = await requestAdminAPI("fetch", {
        table: "employee",
        id: id,
        columns: ["location { name }"],
      });
      setEmployee(response);
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  /* ====================== ADD ATTENDANCE ====================== */
  const addAttendanceHandler = async () => {
    try {
      setLoading(true);
      const coordinates = await getGeolocation();
      await requestAdminAPI("add-attendance", {
        coordinates,
      });
      fetchEmployee();
      setLoading(false);
    } catch (err) {
      setWarning(err.message);
      console.error(err);
    }
  };

  /* ====================== EDIT EMPLOYEE ====================== */
  const submitHandler = async () => {
    try {
      const payload = {
        id: employee?.id,
        phone: document.getElementById("phone").value,
      };
      const files = [
        { name: "photo", file: document.getElementById("image")?.files[0] },
      ];
      setLoading(true);
      for (const file of files) {
        if (!file.file) continue;
        const url = await uploadFile(
          `employees/${employee.id}/${file.name}.${file.file.name
            .split(".")
            .pop()}`,
          file.file,
          () => {}
        );
        payload[file.name] = url;
      }
      await requestAdminAPI("update-employee", payload);
      fetchEmployee();
      setEdit(false);
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEmployee();
  }, []);

  if (!id || (!loading && (!employee || !employee?.id)))
    return (
      <div className="container">
        <p className="text-stone-400">
          Employee ID Not Provided or Employee Not Found!
        </p>
      </div>
    );
  return (
    <div className="container gap-8">
      <ManageEmployeeModal
        open={manage}
        setOpen={setManage}
        employee={employee}
        onComplete={fetchEmployee}
      />
      <Heading title="Employee Info" id={id}>
        {loading ? (
          <LoadingSpinner classes="w-6 h-6 text-stone-200" />
        ) : (
          <Fragment>
            {admin.id === id && (
              <div
                className="text-sm btn-secondary"
                onClick={addAttendanceHandler}
              >
                <p>Check {employee?.checkedIn ? "Out" : "In"}</p>
              </div>
            )}
          </Fragment>
        )}
        {edit ? (
          <Fragment>
            <MdClose className="icon" onClick={() => setEdit(false)} />
            <MdCheck className="icon" onClick={() => submitHandler()} />
          </Fragment>
        ) : (
          <MdEdit className="icon" onClick={() => setEdit(true)} />
        )}
        <MdKey
          className="icon"
          title="Manage Permissions"
          onClick={(e) => navigate(`/employees/permissions?id=${id}`, e)}
        />
        <MdSettings
          className="icon"
          title="Manage Employee"
          onClick={() => setManage(true)}
        />
        <AuditLogs table="employee" id={id} />
        <MdRefresh className="icon" onClick={fetchEmployee} />
      </Heading>
      <div className="grid items-center gap-4 justify-items-center lg:gap-0 lg:space-x-8 lg:flex">
        <ImageSelector
          edit={edit}
          image={employee?.photo}
          classes="rounded-lg w-48 h-36"
          loading={loading}
        />
        <div className="items-end w-full info lg:grid-cols-4">
          <DetailPair
            label="Full Name"
            value={employee?.name}
            loading={loading}
          />
          <DetailPair
            label="Email Address"
            value={employee?.email}
            loading={loading}
          />
          <DetailPair
            label="Department"
            value={toTitleCase(employee.type)}
            loading={loading}
          />
          <DetailPair label="Title" value={employee.title} loading={loading} />
          <DetailPair
            label="Phone Number"
            edit={edit}
            value={employee?.phone}
            inputOptions={{
              required: true,
              defaultValue: employee?.phone,
              id: "phone",
            }}
            loading={loading}
          />
          <DetailPair
            label="Location"
            value={employee.location?.name}
            loading={loading}
          />
          
          <DetailPair
            label="Checked In"
            value={employee?.checkedIn ? "Yes" : "No"}
            loading={loading}
          />
          <DetailPair
            label="Status"
            value={employee.active ? "ACTIVE" : "SUSPENDED"}
            classes={`text-lg font-semibold ${
              employee.active ? "text-green-600" : "text-red-600"
            }`}
            loading={loading}
          />
        </div>
      </div>
      <AttendanceGraph employeeId={id} />
    </div>
  );
}
